import apiClient from './apiClient';

const useMockAuth = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCK_AUTH === 'true';

const mockDashboardStats = () => ({
  agents: {
    total: 4,
    online: 3,
    offline: 1,
  },
  jobs: {
    total: 18,
    success: 14,
    failed: 2,
    running: 2,
  },
  schedules: {
    total: 6,
    active: 5,
  },
  packages: {
    total: 9,
    active: 7,
  },
  recentJobs: [
    {
      id: 'mock-job-1',
      name: 'Sincronizar conexões',
      status: 'success',
      time: new Date(Date.now() - 8 * 60 * 1000).toISOString(),
      successCount: 12,
      failedCount: 0,
    },
    {
      id: 'mock-job-2',
      name: 'Processar automações',
      status: 'running',
      time: new Date(Date.now() - 24 * 60 * 1000).toISOString(),
      successCount: 4,
      failedCount: 0,
    },
    {
      id: 'mock-job-3',
      name: 'Validar credenciais',
      status: 'failed',
      time: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      successCount: 1,
      failedCount: 1,
    },
  ],
});

export const getDashboardStats = async () => {
  if (useMockAuth) {
    return mockDashboardStats();
  }

  try {
    const response = await apiClient.get('/dashboard/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    
    // Never return mock data - always throw errors to avoid fake data in production
    console.error('Dashboard stats API call failed:', error.response?.status, error.response?.data);
    throw new Error(error.response?.data?.detail || error.response?.data?.message || 'Failed to load dashboard data');
  }
};


/**
 * Point-in-time snapshot of the tenant's executor fleet.
 *
 * One request for the whole fleet — counts by status plus the CPU/memory/disk
 * averages the backend computed from the heartbeats it actually received. A
 * metric nobody reported comes back with `average: null`, which callers must
 * render as unavailable rather than as zero. There is no mock fallback here on
 * purpose: an unreachable API is an error, not an empty fleet.
 */
/**
 * Instantaneo da frota de executores do tenant.
 *
 * `includeTrend` liga o `trend_24h` (baldes horarios). Fica desligado por
 * padrao porque o card do Painel faz polling e nao usa o historico -- a
 * varredura extra so e paga por quem pede. Ver PBI 4592.
 */
export const getVmSummary = async ({ includeTrend = false } = {}) => {
  // Sem `includeTrend` a chamada segue com um argumento so, como sempre foi:
  // o card do Painel nao passa a pedir nada novo por causa desta tela.
  const response = includeTrend
    ? await apiClient.get('/dashboard/vm-summary', { params: { include_trend: true } })
    : await apiClient.get('/dashboard/vm-summary');
  return response.data;
};


/**
 * The authenticated user's quick-action selection for the active tenant.
 *
 * Scope comes from the session (secure tenant + user id), never from the
 * caller, so there is nothing to pass in. `null` means "never personalized"
 * and the caller applies the catalog default — an empty array is a real,
 * saved choice.
 *
 * @param {{ signal?: AbortSignal }} [options] abort when the tenant changes
 * @returns {Promise<string[]|null>}
 */
export const getDashboardQuickActions = async ({ signal } = {}) => {
  if (useMockAuth) {
    return null;
  }

  const response = await apiClient.get('/dashboard/quick-actions', { signal });
  return response.data?.action_ids ?? null;
};

/**
 * Persist the selection. Errors propagate on purpose: the caller has to roll
 * its optimistic state back instead of reporting a save that never happened.
 *
 * @param {string[]} actionIds ordered catalog ids (max 4)
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<string[]>} ids as stored by the backend
 */
export const saveDashboardQuickActions = async (actionIds, { signal } = {}) => {
  if (useMockAuth) {
    return Array.isArray(actionIds) ? [...actionIds] : [];
  }

  const response = await apiClient.put(
    '/dashboard/quick-actions',
    { action_ids: actionIds },
    { signal }
  );
  return response.data?.action_ids ?? actionIds;
};

export const getDashboardAnalytics = async (days = 30) => {
  if (useMockAuth) {
    return {
      days,
      jobs: [],
      success_rate: 0,
      average_duration: 0,
    };
  }

  try {
    const response = await apiClient.get('/analytics/dashboard', {
      params: { days }
    });
    return response.data;
  } catch (error) {
    // Error fetching dashboard analytics
    throw new Error(error.response?.data?.detail || 'Failed to load dashboard analytics');
  }
};
